import _find from "lodash/find";
import _get from "lodash/get";
import _uniq from "lodash/uniq";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import {
    setSelectedPhoneNumber,
    fetchAvailableNumbers,
    resetCurrentPhoneNumberCheckMessages,
} from "./phoneNumberActions";
import {
    API_URL_CHECK_CUSTOMER_AND_SET_PERSONAL_DETAILS_FOR_NEW_MEMBER,
    API_URL_CHECK_PHONE_NUMBER_FOR_NEW_MEMBER,
    API_URL_FETCH_PRICE_PLANS_FOR_SUBSCRIPTION,
    API_URL_LOOKUP_CUSTOMER_BY_PHONE_NUMBER,
    CART_ITEM_TYPE,
    CUSTOMER_TYPE,
    CustomerType,
    FORM_ID,
    FORMATTED_PHONE_NUMBER_LEN,
} from "../../../../../constants";
import { ICartItemWithBundleId, RootState } from "../../../../../models";
import { Agent, ChangeScope } from "../../../../../models/state";
import { CheckCustomerAndSetPersonalDetailsForNewMemberRequestBody } from "../../../../../models/actionPayloads";
import { post as Ajax_post } from "../../../app/Ajax";
import { setNegativeFlash } from "../../../app/state/generalActions";
import { setActiveSubscription } from "../../../checkout/state/actions/cartActions";
import { CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER } from "../../../checkout/state/actionTypes";
import * as AT from "../actionTypes";
import { cartItemReducer } from "../../../checkout/state/selectors/cartSelectors";
import { hasReachedMaximumNumberOfNewSubscriptions } from "../../../checkout/state/selectors/combinedSelectors";
import {
    getPhoneNumberCheckType,
    getSelectedPhoneNumber,
} from "../../../checkout/state/selectors/phoneNumberSelectors";
import {
    getProductUser,
    getSubscription,
    getSubscriptionMaxAgeAllowed,
    isCatalogListingOk as isCatalogListingOkSelector,
    isEsimSelected as isEsimSelectedSelector,
    isProductUserDifferentThanLegalOwner as isProductUserDifferentThanLegalOwnerSelector,
    isServiceSubscription as isServiceSubscriptionSelector,
    isSimFromStoreSelected as isSimFromStoreSelectedSelector,
} from "../../../checkout/state/selectors/subscriptionsSelectors";
import { isValidSubscriptionAge } from "../../../checkout/utils";
import { actions as RAPIDFORM_ACTIONS, actionTypes as RF_AT } from "../../../rapidform";
import { allValidationRules, maskField, VALIDATION_FIELD } from "../../../rapidform/formValidation";
import TEXT from "../../text-consts";
import { fulfilled, isPhoneNumberUsable } from "../../utils";
import { setValue } from "../actions";
import {
    RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
    RETAILER_FETCH_AVAILABLE_PHONE_NUMBERS,
    RETAILER_SET_CUSTOMER_DETAILS,
    RETAILER_SET_SELECTED_ACCOUNT_ID,
    RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER,
} from "../actionTypes";
import { API_URL_RETAILER_SET_ACCOUNT } from "../../api";
import * as CUSTOMER_INFO_SELECTORS from "../selectors/customerInfoSelectors";
import { getCustomerSelectedAccountId, getExistingSubscriptionForChange } from "../selectors/customerSelectors";
import { getFormById } from "../selectors/formSelectors";
import { getAvailablePhoneNumbers, getSelectedPanel } from "../selectors/phoneNumbersSelectors";
import { isMhsSubscription } from "../../../../../shared/subscriptionCodeUtils";

export const lookupCustomerByPhoneNumber =
    (
        phoneNumber: string,
        agent?: Agent | null,
    ): ThunkAction<Promise<{ success: boolean }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const onlyDigits = phoneNumber.replace(/\s/g, "");

        if (!isPhoneNumberUsable(onlyDigits)) {
            dispatch(setNegativeFlash(TEXT.ERRORS.INVALID_PHONE_NUMBER_FORMAT));
            return { success: false };
        }

        const payload = Ajax_post<{ success: boolean }>(
            {
                url: API_URL_LOOKUP_CUSTOMER_BY_PHONE_NUMBER,
                data: {
                    phoneNumber: onlyDigits,
                    agent: agent ?? undefined,
                },
            },
            dispatch,
        );

        dispatch({
            type: RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER,
            payload,
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERRORS.CHECK_PHONE_NUMBER,
                },
            },
        });

        return payload;
    };

export const fetchPricePlansForEdit =
    (phoneNumber: string, memberId?: string): ThunkAction<Promise<unknown>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const payload = Ajax_post(
            {
                url: API_URL_FETCH_PRICE_PLANS_FOR_SUBSCRIPTION,
                data: { phoneNumber, memberId },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_FETCH_PRICE_PLANS_FOR_EDIT,
            payload,
        });

        return payload;
    };

export const checkCustomerPhoneNumber =
    (
        phoneNumber?: string,
        memberId?: string,
        activePanel?: CustomerType,
        keepExistingSubscription = false,
        scopedChangeType?: ChangeScope,
    ): ThunkAction<Promise<unknown>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const onlyDigits = (phoneNumber ?? "").replace(/\s/g, "");

        if (!isPhoneNumberUsable(onlyDigits)) {
            dispatch(setNegativeFlash(TEXT.ERRORS.INVALID_PHONE_NUMBER_FORMAT));
            return Promise.resolve();
        }

        const payload = Ajax_post(
            {
                url: API_URL_CHECK_PHONE_NUMBER_FOR_NEW_MEMBER,
                data: {
                    phoneNumber: onlyDigits,
                    memberId,
                    activePanel,
                    keepExistingSubscription,
                    scopedChangeType,
                },
            },
            dispatch,
        );

        dispatch({
            type: CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER,
            payload,
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERRORS.CHECK_PHONE_NUMBER,
                },
            },
        });

        return payload;
    };

export const chooseCustomerPhoneNumber =
    (newPhoneNumber: string, memberId?: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const activePanel = getSelectedPanel(state);
        const customerType = activePanel || CUSTOMER_TYPE.EXISTING;
        const selectedPhoneNumber = getSelectedPhoneNumber(state, memberId);

        // If called with empty string but there's already a valid phone number, don't override it
        if (!newPhoneNumber && selectedPhoneNumber && isPhoneNumberUsable(selectedPhoneNumber)) {
            return;
        }

        const validationRule = allValidationRules[VALIDATION_FIELD.SUBSCRIPTION_NUMBER];

        const maskedNumber = maskField((newPhoneNumber || "").replace(/\s/g, ""), selectedPhoneNumber, validationRule);

        if (maskedNumber !== selectedPhoneNumber) {
            dispatch(setSelectedPhoneNumber(maskedNumber, customerType, memberId));
        }

        // Here it is either port-in or existing customer.
        // Maybe check phone number
        if (([CUSTOMER_TYPE.EXISTING, CUSTOMER_TYPE.PORT_IN] as CustomerType[]).includes(activePanel)) {
            if (maskedNumber.length >= FORMATTED_PHONE_NUMBER_LEN && maskedNumber !== selectedPhoneNumber) {
                return dispatch(checkCustomerPhoneNumber(maskedNumber, memberId, activePanel));
            }
        }
    };

export const customerSwitchPhoneNumberPanel =
    (activePanel: CustomerType, memberId?: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const currentSub = getSubscription(state, memberId);
        if (
            activePanel === CUSTOMER_TYPE.NEW &&
            hasReachedMaximumNumberOfNewSubscriptions(state) &&
            isMhsSubscription(currentSub?.code)
        ) {
            return dispatch(setNegativeFlash(TEXT.ERRORS.MAX_NUMBER_OF_NEW_NUMBERS_REACHED));
        }

        dispatch(resetCurrentPhoneNumberCheckMessages());

        // Reset number immediately for PORT_IN and EXISTING to prevent new number being shown in input field
        if (([CUSTOMER_TYPE.EXISTING, CUSTOMER_TYPE.PORT_IN] as CustomerType[]).includes(activePanel)) {
            dispatch(setSelectedPhoneNumber(""));
        }

        // Reset available numbers so they are not temporary shown while fetching new ones.
        if (activePanel === CUSTOMER_TYPE.NEW) {
            dispatch({
                type: fulfilled(RETAILER_FETCH_AVAILABLE_PHONE_NUMBERS),
                payload: [],
            });
        }

        // Set panel side, clear form and reset memberId in order
        // If switching to new number panel => fetch new numbers and set first to selected
        dispatch({ type: RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL, activePanel });

        const isProductUserDifferent = isProductUserDifferentThanLegalOwnerSelector(state, memberId);

        if (!isProductUserDifferent) {
            dispatch(RAPIDFORM_ACTIONS.clearAll(FORM_ID.CUSTOMER_PRODUCT_USER));
        }

        if (activePanel === CUSTOMER_TYPE.NEW) {
            dispatch(fetchAvailableNumbers(true, memberId));
        }

        if (activePanel === CUSTOMER_TYPE.EXISTING) {
            dispatch(chooseCustomerPhoneNumber("", memberId));
        }
    };

export const prefillCustomerForm =
    (id?: string, removed = false, editMode = false): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();

        const productUser = getProductUser(state, id);
        const productUserPhoneNumber = getSelectedPhoneNumber(state, id);
        const productUserType = getPhoneNumberCheckType(state, id);
        const availablePhoneNumbers = getAvailablePhoneNumbers(state);
        const isServiceSubscription = isServiceSubscriptionSelector(state, id);
        const defaultPanel = isServiceSubscription ? CUSTOMER_TYPE.NEW : CUSTOMER_TYPE.PORT_IN;
        const activePanel = productUserType || defaultPanel;

        dispatch({ type: RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL, activePanel });
        if (!removed) {
            dispatch(setSelectedPhoneNumber(productUserPhoneNumber, productUserType || activePanel, id, editMode));

            if (activePanel === CUSTOMER_TYPE.NEW && !!productUserPhoneNumber) {
                dispatch({
                    type: fulfilled(RETAILER_FETCH_AVAILABLE_PHONE_NUMBERS),
                    payload: _uniq([productUserPhoneNumber, ...availablePhoneNumbers]),
                });
            } else if (activePanel === CUSTOMER_TYPE.NEW && availablePhoneNumbers.length === 0) {
                dispatch(fetchAvailableNumbers(true, id));
            }
        }

        // Prefill customer preferences and SIM type selections
        if (!removed) {
            const isProductUserDifferent = isProductUserDifferentThanLegalOwnerSelector(state, id);
            const isEsim = isEsimSelectedSelector(state, id);
            const isSimFromStore = isSimFromStoreSelectedSelector(state, id);
            const catalogOk = isCatalogListingOkSelector(state, id);

            dispatch(setValue("isProductUserDifferentThanLegalOwner", isProductUserDifferent));
            dispatch(setValue("isEsimSelected", isEsim));
            dispatch(setValue("isSimFromStoreSelected", isSimFromStore));
            dispatch(setValue("isCatalogListingOk", catalogOk));
        }

        const mapper = (field: string) => {
            const internalValueMap: Record<string, string> = {
                [VALIDATION_FIELD.PRODUCT_USER_EMAIL]: "email",
            };
            return internalValueMap[field] || field;
        };
        const productUserValues = productUser as Record<string, string | undefined>;

        [
            VALIDATION_FIELD.BIRTHDATE,
            VALIDATION_FIELD.FIRST_NAME,
            VALIDATION_FIELD.LAST_NAME,
            VALIDATION_FIELD.STREET,
            VALIDATION_FIELD.POSTCODE,
            VALIDATION_FIELD.CITY,
            VALIDATION_FIELD.PRODUCT_USER_EMAIL,
        ].forEach((field) => {
            const mappedField = mapper(field);
            const value = removed ? "" : productUserValues[mappedField];
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_PRODUCT_USER),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.CUSTOMER_PRODUCT_USER,
                    inputName: `user-${field}`,
                    inputValue: value,
                    visible: true,
                },
            });
        });
    };

interface CustomerDetailsResponse {
    hasUpdatedCustomer?: boolean;
    isNewNumberWithPotentialFraud?: boolean;
    minUpfrontPrice?: number;
    isNotFound?: boolean;
    isZipNeeded?: boolean;
}

export const setCustomerDetails =
    ({
        customerId,
        campaignId,
        isOptingOutForTeliaXServices = false,
    }: {
        campaignId?: string;
        customerId: string;
        isOptingOutForTeliaXServices?: boolean;
    }): ThunkAction<Promise<CustomerDetailsResponse>, RootState, unknown, AnyAction> =>
    async (dispatch, getState): Promise<CustomerDetailsResponse> => {
        const state = getState();
        const { values } = getFormById(state, FORM_ID.CUSTOMER_PRODUCT_USER);
        const isCatalogListingOk = CUSTOMER_INFO_SELECTORS.isCatalogListingOk(state);
        const isSecretNumber = CUSTOMER_INFO_SELECTORS.isSecretNumber(state);
        const isEsimSelected = CUSTOMER_INFO_SELECTORS.isEsimSelected(state);
        const isKeepCurrentSim = CUSTOMER_INFO_SELECTORS.isKeepCurrentSim(state);
        const isPhysicalSimSelected = CUSTOMER_INFO_SELECTORS.isPhysicalSimSelected(state);
        const isSimFromStoreSelected = CUSTOMER_INFO_SELECTORS.isSimFromStoreSelected(state);
        const storeSimNumber = CUSTOMER_INFO_SELECTORS.storeSimNumber(state);
        const eid = CUSTOMER_INFO_SELECTORS.eid(state);
        const esimType = CUSTOMER_INFO_SELECTORS.esimType(state);
        const isBarringsUsed = CUSTOMER_INFO_SELECTORS.isBarringsUsed(state);
        const isCampaignToggled = CUSTOMER_INFO_SELECTORS.isCampaignToggled(state);
        const isProductUserDifferentThanLegalOwner =
            CUSTOMER_INFO_SELECTORS.isProductUserDifferentThanLegalOwner(state);
        const birthDate = isProductUserDifferentThanLegalOwner
            ? _get(values, `user.${VALIDATION_FIELD.BIRTHDATE}`, null)
            : CUSTOMER_INFO_SELECTORS.getLegalOwnerBirthdate(state);
        const isEmailUsedForTeliaXServices =
            CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaTrygg(state) ||
            CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaSky(state);
        const isServiceSubscription = isServiceSubscriptionSelector(state, customerId);
        const selectedAccountId = getCustomerSelectedAccountId(state);
        const existingSubscriptionForChange = getExistingSubscriptionForChange(state);

        const subscriptionMaxAgeAllowed = getSubscriptionMaxAgeAllowed(state, customerId);

        // Check if age is ok - for age-restricted subscriptions, always validate even when legal owner = product user
        // This is important for existing customers with JUNIOR/BARN subscriptions who may have aged out
        const isProductUserAgeOk = isValidSubscriptionAge({
            subscriptionMaxAgeAllowed,
            productUserBirthDate: birthDate,
            useProductUserAge: true,
        });

        if (!isProductUserAgeOk && subscriptionMaxAgeAllowed) {
            dispatch(setValue("isProductUserAgeOk", false));
            dispatch(
                setNegativeFlash(
                    `Ved kjøp av dette abonnementet må bruker av abonnementet være under ${subscriptionMaxAgeAllowed} år.`,
                ),
            );
            return {
                hasUpdatedCustomer: false,
            };
        } else {
            dispatch(setValue("isProductUserAgeOk", true));
        }

        const productUser = {
            firstName: values.user[VALIDATION_FIELD.FIRST_NAME],
            lastName: values.user[VALIDATION_FIELD.LAST_NAME],
            street: values.user[VALIDATION_FIELD.STREET],
            city: values.user[VALIDATION_FIELD.CITY],
            postcode: values.user[VALIDATION_FIELD.POSTCODE],
            email: values.user[VALIDATION_FIELD.PRODUCT_USER_EMAIL] || "",
            birthDate: values.user[VALIDATION_FIELD.BIRTHDATE],
        };

        let teliaXServicesEmail;
        if (isEmailUsedForTeliaXServices || isServiceSubscription) {
            teliaXServicesEmail = productUser.email;
        }

        const memberContainer: CheckCustomerAndSetPersonalDetailsForNewMemberRequestBody = {
            // @ts-expect-error: fields in productUser is any
            productUser,
            phoneNumber: {
                number: (values.user.number || "").replace(/\s/g, ""),
                type: values.user.type || null,
            },
            isCatalogListingOk,
            isEsimSelected,
            isKeepCurrentSim: isKeepCurrentSim || undefined,
            isPhysicalSimSelected: isPhysicalSimSelected || undefined,
            isSimFromStoreSelected: isSimFromStoreSelected || undefined,
            storeSimNumber: isSimFromStoreSelected ? storeSimNumber : undefined,
            eid: eid || undefined,
            esimType,
            isBarringToggleOn: isBarringsUsed,
            isProductUserDifferentThanLegalOwner,
            campaignId: isCampaignToggled ? campaignId : undefined,
            newMemberId: customerId,
            existingMemberId: customerId,
            isSecretNumber,
            isOptingOutForTeliaXServices,
            teliaXServicesEmail,
            selectedAccountId,
            changeScope: existingSubscriptionForChange?.changeScope,
            keepExistingSubscription:
                existingSubscriptionForChange?.changeScope === "sim" ||
                existingSubscriptionForChange?.changeScope === "productUser" ||
                existingSubscriptionForChange?.changeScope === "subscriptionChange"
                    ? true
                    : undefined,
        };

        const setCustomerPromise = Ajax_post<{
            cart: { content?: ICartItemWithBundleId[] };
            hasUpdatedCustomer?: boolean;
            isEmailTakenForTeliaSky?: boolean;
            isEmailTakenForTeliaTrygg?: boolean;
            isNewNumberWithPotentialFraud?: boolean;
            isNotFound?: boolean;
            isZipNeeded?: boolean;
            minUpfrontPrice?: number;
            shouldAddTeliaSky?: boolean;
            shouldAddTeliaTrygg?: boolean;
        }>(
            {
                url: API_URL_CHECK_CUSTOMER_AND_SET_PERSONAL_DETAILS_FOR_NEW_MEMBER,
                data: memberContainer,
            },
            dispatch,
        );

        setCustomerPromise.then((payload) => {
            if (payload.isEmailTakenForTeliaSky || payload.isEmailTakenForTeliaTrygg) {
                return payload;
            }

            const minUpfrontPrice =
                typeof payload.minUpfrontPrice === "number" && Number.isFinite(payload.minUpfrontPrice)
                    ? payload.minUpfrontPrice
                    : undefined;

            if (payload.isNewNumberWithPotentialFraud || (typeof minUpfrontPrice === "number" && minUpfrontPrice > 0)) {
                return {
                    isNewNumberWithPotentialFraud: !!payload.isNewNumberWithPotentialFraud,
                    minUpfrontPrice,
                };
            }

            const isZipNeeded = !!payload.isZipNeeded;
            const isNotFound = !!payload.isNotFound;

            dispatch(setValue("isZipNeeded", isZipNeeded));
            dispatch(setValue("isNotFound", isNotFound));

            if (isZipNeeded || isNotFound) {
                return { isZipNeeded, isNotFound };
            }

            // find first draft
            const cartContent = _get(payload, "cart.content", []) as ICartItemWithBundleId[];
            const draftItem = _find(
                // We need to get flattened content to support navigate to next member card for prepaid and sim deals
                cartContent.reduce(cartItemReducer, []),
                (item) => item.type === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT,
            );

            const currentActiveSubscription = getState().checkout?.cart?.activeSubscription;
            if (draftItem && !currentActiveSubscription) {
                dispatch(setValue("isProductUserDifferentThanLegalOwner", false));
                dispatch(setValue("isCatalogListingOk", false));
                dispatch(setValue("isSecretNumber", false));
                dispatch(setValue("isCampaignToggled", false));
                dispatch(setValue("campaignIdsNotAllowed", []));
                dispatch(setValue("isOptingOutForTeliaXServices", false));
                dispatch(setValue("isEmailTakenForTeliaTrygg", false));
                dispatch(setValue("isEmailTakenForTeliaSky", false));
                dispatch(setSelectedPhoneNumber("", CUSTOMER_TYPE.NEW));
                dispatch({
                    type: fulfilled(RETAILER_FETCH_AVAILABLE_PHONE_NUMBERS),
                    payload: [],
                });
                dispatch({
                    type: RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
                    activePanel: CUSTOMER_TYPE.NEW,
                });
                dispatch(setActiveSubscription(draftItem.bundleId));
                dispatch(fetchAvailableNumbers(true, draftItem.bundleId));
                dispatch(prefillCustomerForm(draftItem.bundleId, true));
            }

            return { hasUpdatedCustomer: true };
        });

        dispatch({
            type: RETAILER_SET_CUSTOMER_DETAILS,
            payload: setCustomerPromise,
        });

        return setCustomerPromise.then((data) => {
            if (data.isEmailTakenForTeliaSky || data.isEmailTakenForTeliaTrygg) {
                return data;
            }

            if (data.isNewNumberWithPotentialFraud || typeof data.minUpfrontPrice === "number") {
                return {
                    isNewNumberWithPotentialFraud: !!data.isNewNumberWithPotentialFraud,
                    minUpfrontPrice: data.minUpfrontPrice,
                };
            }

            if (data.isZipNeeded || data.isNotFound) {
                return { isZipNeeded: data.isZipNeeded, isNotFound: data.isNotFound };
            }

            return { hasUpdatedCustomer: true };
        });
    };

export const setSelectedAccountId =
    (accountId?: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch): void => {
        dispatch({
            type: RETAILER_SET_SELECTED_ACCOUNT_ID,
            accountId,
        });

        if (accountId) {
            Ajax_post<{ success: boolean }>(
                {
                    url: API_URL_RETAILER_SET_ACCOUNT,
                    data: { accountId },
                },
                dispatch,
            );
        }
    };
